import { useManualTenderDrawer } from '@/lib/services/cash-drawers/useManualTenderDrawer'
import { CustomerNotificationSwitch } from '@/components/orders/CustomerNotificationSwitch'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { PaymentType } from '@/lib/enums/PaymentType'
import { PaymentStatus } from '@/lib/enums/PaymentStatus'
import { useAbility } from '@/lib/casl'
import { getErrorMessage, parseCurrency } from '@/lib/utils'
import { useOpenCashDrawers } from '@/lib/services/cash-drawers/cash-drawers.query'
import { manualTenderLocationId } from '@/lib/services/cash-drawers/cash-drawers.utils'
import {
  outcomeReasonRequiresNote,
  salesOrderOutcomeReasonOptions,
} from '@/lib/sales-order-outcome-reasons'
import { useParams } from '@tanstack/react-router'
import { AlertTriangle, DollarSignIcon, SendIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { CashDrawerStatusError } from '@/components/pos/CashDrawerStatusError'
import {
  CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE,
  refreshManualTenderDrawer,
  cashDrawerQueriesAreFetching,
  cashDrawerQueriesHaveError,
  retryFailedCashDrawerQueries,
} from '@/lib/services/cash-drawers/cash-drawers.preflight'

export function salesOrderCancellationNeedsTill(order) {
  const manualTenderTotal = (order?.payment_snapshot?.payments ?? [])
    .filter(
      (payment) =>
        payment?.status === PaymentStatus.Paid.Value &&
        [PaymentType.Cash.Value, PaymentType.Check.Value].includes(payment?.type)
    )
    .reduce(
      (total, payment) => total + parseCurrency(payment?.amount_applied ?? payment?.amount),
      0
    )

  return Math.round(manualTenderTotal * 100) > 0
}

function SalesOrderCancelDialog({
  open,
  onOpenChange,
  order,
  paidTotal,
  canNotifyCustomerOnCancel,
  sendCustomerEmail,
  onSendCustomerEmailChange,
  cancelReason,
  onCancelReasonChange,
  cancelReasonNote,
  onCancelReasonNoteChange,
  onCancelOrder,
  isExternalFinancialOrder = false,
  externalProviderLabel = 'external source',
}) {
  const { orgId } = useParams({ strict: false })
  const ability = useAbility()
  const canOperateCashDrawers = ability?.can('operate', 'cash-drawers') === true
  const cancellationNeedsTill = !isExternalFinancialOrder && salesOrderCancellationNeedsTill(order)
  const orderLocationId = manualTenderLocationId(order)
  const {
    statusQuery: cashDrawerStatusQuery,
    trackingEnabled,
    workstationsQuery,
    workstationId,
    pairedWorkstation,
    currentDrawerQuery,
    resolvedDrawer: pairedDrawer,
    isLoading: cashDrawerQueriesLoading,
    pairingBlocker,
  } = useManualTenderDrawer({
    organizationId: orgId,
    targetLocationId: orderLocationId,
    canOperateCashDrawers,
    enabled: open && cancellationNeedsTill,
  })
  // Cancellation alone permits the sole open till at the order's location without pairing.
  const openDrawersQuery = useOpenCashDrawers({
    enabled: open && cancellationNeedsTill && trackingEnabled && canOperateCashDrawers,
  })
  const openDrawersForOrder = (openDrawersQuery.data?.data ?? []).filter(
    (session) => Number(session.location?.id) === orderLocationId
  )
  const soleOpenDrawerForOrder = openDrawersForOrder.length === 1 ? openDrawersForOrder[0] : null
  const hasUsableDrawer = pairedWorkstation
    ? Boolean(pairedDrawer)
    : Boolean(soleOpenDrawerForOrder)
  const cashDrawerQueryError =
    !hasUsableDrawer &&
    cashDrawerQueriesHaveError(
      cashDrawerStatusQuery,
      workstationsQuery,
      currentDrawerQuery,
      openDrawersQuery
    )
  const isCashDrawerLoading =
    cancellationNeedsTill &&
    (cashDrawerQueriesLoading ||
      (trackingEnabled && canOperateCashDrawers && openDrawersQuery.isLoading))
  const cashDrawerBlocker = !canOperateCashDrawers
    ? "You don't have permission to record the cash or check refund for this cancellation."
    : hasUsableDrawer
      ? null
      : pairingBlocker
  const [isCheckingCashDrawer, setIsCheckingCashDrawer] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const cancellationBlocked = cancellationNeedsTill && (isCashDrawerLoading || cashDrawerQueryError)
  const handleCancelReasonChange = (nextReason) => {
    onCancelReasonChange(nextReason)
    if (!outcomeReasonRequiresNote(nextReason)) {
      onCancelReasonNoteChange('')
    }
  }
  const handleCancelOrder = async () => {
    setIsCheckingCashDrawer(cancellationNeedsTill && trackingEnabled)

    try {
      let cashDrawerSessionId = null

      if (cancellationNeedsTill) {
        const drawer = await refreshManualTenderDrawer({
          allowSoleOpenSession: true,
          canOperateCashDrawers,
          currentDrawerQuery,
          organizationId: orgId,
          openDrawersQuery,
          pairedWorkstation,
          statusQuery: cashDrawerStatusQuery,
          targetLocationId: orderLocationId,
          workstationId,
          workstationsQuery,
        })
        cashDrawerSessionId = drawer.session?.id ?? null
      }
      setIsCheckingCashDrawer(false)
      setIsCancelling(true)
      await onCancelOrder({ cashDrawerSessionId })
    } catch (error) {
      toast.error(
        error?.message || getErrorMessage(error) || CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE
      )
    } finally {
      setIsCheckingCashDrawer(false)
      setIsCancelling(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!(isCheckingCashDrawer || isCancelling) || nextOpen) onOpenChange(nextOpen)
      }}
    >
      <DialogContent size="small">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-balance">
            <AlertTriangle className="size-4 shrink-0 text-destructive" />
            {isExternalFinancialOrder ? 'Request cancellation?' : 'Cancel this order?'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Canceling this order cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-status-destructive-border bg-status-destructive-background p-3">
            <Typography variant="label">This action cannot be undone.</Typography>
            <div className="mt-2 grid gap-2 text-sm text-muted-foreground">
              {isExternalFinancialOrder ? (
                <>
                  <span className="flex items-start gap-2">
                    <SendIcon className="size-4 shrink-0 text-interaction" /> Send the request to{' '}
                    {externalProviderLabel}
                  </span>
                  <span className="flex items-start gap-2">
                    <DollarSignIcon className="size-4 shrink-0 text-muted-foreground" /> No local
                    refund is processed
                  </span>
                </>
              ) : (
                <>
                  {order.payment_status === SalesOrderPaymentStatus.Paid.Value ? (
                    <span className="flex items-start gap-2">
                      <DollarSignIcon className="size-4 shrink-0 text-success" /> Process a full
                      refund
                    </span>
                  ) : paidTotal > 0.01 ? (
                    <span className="flex items-start gap-2">
                      <DollarSignIcon className="size-4 shrink-0 text-success" /> Refund collected
                      payments
                    </span>
                  ) : null}
                  {canNotifyCustomerOnCancel ? (
                    <CustomerNotificationSwitch
                      notifyCustomer={sendCustomerEmail}
                      onNotifyCustomerChange={onSendCustomerEmailChange}
                    />
                  ) : null}
                </>
              )}
              <span className="flex items-start gap-2">
                <XIcon className="size-4 shrink-0 text-destructive" /> Cancel this order
              </span>
              {cancellationNeedsTill && cashDrawerQueryError ? (
                <CashDrawerStatusError
                  isRetrying={cashDrawerQueriesAreFetching(
                    cashDrawerStatusQuery,
                    workstationsQuery,
                    currentDrawerQuery,
                    openDrawersQuery
                  )}
                  onRetry={() =>
                    retryFailedCashDrawerQueries(
                      cashDrawerStatusQuery,
                      workstationsQuery,
                      currentDrawerQuery,
                      openDrawersQuery
                    )
                  }
                />
              ) : cancellationNeedsTill && (trackingEnabled || isCashDrawerLoading) ? (
                <Typography variant="supporting" tone={cashDrawerBlocker ? 'destructive' : 'muted'}>
                  {isCashDrawerLoading
                    ? "Checking this device's cash drawer..."
                    : cashDrawerBlocker ||
                      (pairedDrawer
                        ? `Cash and check refunds will use ${pairedWorkstation.name}.`
                        : `Cash and check refunds will use ${soleOpenDrawerForOrder.workstation.name}, the only cash drawer in use at this order location.`)}
                </Typography>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <div className="flex min-h-7 items-center justify-between gap-2">
                <Label htmlFor="cancel-order-reason">Reason</Label>
                {cancelReason ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="micro"
                    onClick={() => {
                      onCancelReasonChange('')
                      onCancelReasonNoteChange('')
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
              <Select value={cancelReason || ''} onValueChange={handleCancelReasonChange}>
                <SelectTrigger id="cancel-order-reason" aria-label="Reason" className="w-full">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {salesOrderOutcomeReasonOptions.map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {outcomeReasonRequiresNote(cancelReason) ? (
              <div className="grid gap-1.5">
                <Label htmlFor="cancel-order-reason-note">Note</Label>
                <Textarea
                  id="cancel-order-reason-note"
                  value={cancelReasonNote}
                  onChange={(event) => onCancelReasonNoteChange(event.target.value)}
                  maxLength={500}
                  rows={3}
                />
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isCheckingCashDrawer || isCancelling}
            onClick={() => onOpenChange(false)}
          >
            Keep Order
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleCancelOrder}
            disabled={cancellationBlocked || isCheckingCashDrawer || isCancelling}
          >
            {isCancelling
              ? 'Cancelling...'
              : isCashDrawerLoading || isCheckingCashDrawer
                ? 'Checking cash drawer...'
                : isExternalFinancialOrder
                  ? 'Request Cancellation'
                  : 'Cancel Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export { SalesOrderCancelDialog }
